/**
 * Governance Hook
 *
 * FERP CRITICAL: Repository pattern integration for 5 governance primitives
 * Provides centralized access to governance repositories with audit trail
 *
 * FERP Principles:
 * - Repository pattern (NO direct GraphQL calls)
 * - Backend ID generation (useGenerateId for all entity IDs)
 * - Data-driven (all governance data from DynamoDB/Redis)
 * - Life-critical (immutable audit trail for all governance changes)
 * - Domain-agnostic (works across healthcare/finance/legal)
 *
 * 5 Governance Primitives:
 * 1. EscalationPolicy - Escalation paths for workflow failures
 * 2. WorkflowSchedule - Time-based workflow scheduling (cron, interval, one-time)
 * 3. ResourceReservation - Resource pool management (compute, licenses, experts)
 * 4. IntegrationConfig - External system health monitoring
 * 5. PolicyRule - Business rule definitions with dry-run testing
 */

import { useState, useCallback } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { useCurrentTenant } from '../../hooks/useCurrentTenant';
import { useAuth } from '../../security/useAuth';
import { notifications } from '@mantine/notifications';
import { createPolicyRepository } from '../../repositories/PolicyRepository';
import type {
  CreatePolicyRuleInput,
  UpdatePolicyRuleInput,
  DeletePolicyRuleInput,
  DryRunPolicyInput,
} from '../../repositories/PolicyRepository';

/**
 * Escalation Policy Interface
 */
export interface EscalationPolicy {
  escalation_id: string;
  tenant_id: string;
  workflow_type: string;
  escalation_levels: EscalationLevel[];
  auto_escalate: boolean;
  escalation_interval_minutes: number;
  final_fallback_role: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface EscalationLevel {
  level: number;
  role: string;
  timeout_minutes: number;
  notification_channels: string[];
}

/**
 * Workflow Schedule Interface
 */
export interface WorkflowSchedule {
  schedule_id: string;
  tenant_id: string;
  workflow_id: string;
  workflow_name: string;
  schedule_type: 'Cron' | 'Interval' | 'OneTime';
  cron_expression?: string;
  interval_minutes?: number;
  start_time: string;
  end_time?: string;
  enabled: boolean;
  next_execution?: string;
  timezone: string;
  created_at: string;
  updated_at: string;
}

/**
 * Resource Reservation Interface
 */
export interface ResourceReservation {
  resource_id: string;
  tenant_id: string;
  resource_type: 'Compute' | 'License' | 'Expert' | 'Custom';
  resource_name: string;
  total_capacity: number;
  reserved: number;
  available: number;
  reservation_policies: ReservationPolicy[];
  utilization_threshold: number;
  created_at: string;
  updated_at: string;
}

export interface ReservationPolicy {
  policy_id: string;
  priority: number;
  max_reservation_per_user: number;
  auto_release_minutes: number;
}

/**
 * Integration Config Interface
 */
export interface IntegrationConfig {
  integration_id: string;
  tenant_id: string;
  service_name: string;
  endpoint_url: string;
  auth_type: 'OAuth' | 'ApiKey' | 'BasicAuth' | 'None';
  auth_config: Record<string, any>;
  health_check_interval_seconds: number;
  timeout_seconds: number;
  status: 'Healthy' | 'Degraded' | 'Down';
  last_health_check?: string;
  response_time_ms?: number;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Policy Rule Interface
 */
export interface PolicyRule {
  policy_id: string;
  tenant_id: string;
  policy_type: string;
  policy_name: string;
  conditions: PolicyCondition[];
  actions: PolicyAction[];
  enabled: boolean;
  priority: number;
  created_at: string;
  updated_at: string;
}

export interface PolicyCondition {
  field: string;
  operator: 'equals' | 'not_equals' | 'greater_than' | 'less_than' | 'contains' | 'regex';
  value: any;
}

export interface PolicyAction {
  type: 'notify' | 'escalate' | 'block' | 'approve' | 'custom';
  params: Record<string, any>;
}

export interface DryRunResult {
  policy_id: string;
  matched: boolean;
  conditions_evaluated: Array<{
    condition: PolicyCondition;
    matched: boolean;
    reason?: string;
  }>;
  actions_to_execute: PolicyAction[];
  zen_dsl?: string; // Zen DSL representation (for debugging)
}

/**
 * Governance Hook
 *
 * FERP Pattern: Repository pattern for all 5 governance primitives
 */
export function useGovernance() {
  const apolloClient = useApolloClient();
  const { tenantId } = useCurrentTenant();
  const { user } = useAuth();

  const [escalationPolicies, setEscalationPolicies] = useState<EscalationPolicy[]>([]);
  const [workflowSchedules, setWorkflowSchedules] = useState<WorkflowSchedule[]>([]);
  const [resourceReservations, setResourceReservations] = useState<ResourceReservation[]>([]);
  const [integrationConfigs, setIntegrationConfigs] = useState<IntegrationConfig[]>([]);
  const [policyRules, setPolicyRules] = useState<PolicyRule[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ============================================================
  // Escalation Policy Operations
  // ============================================================

  const getEscalationPolicies = useCallback(async (): Promise<EscalationPolicy[]> => {
    if (!tenantId) return [];

    setIsLoading(true);
    try {
      // TODO: Use EscalationRepository when integrated
      // const policies = await escalationRepository.findByTenant(tenantId);
      const policies: EscalationPolicy[] = [];
      setEscalationPolicies(policies);
      return policies;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to load escalation policies';
      setError(errorMsg);
      notifications.show({
        title: 'Error',
        message: errorMsg,
        color: 'red',
      });
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  const createEscalationPolicy = useCallback(async (
    policy: Omit<EscalationPolicy, 'escalation_id' | 'created_at' | 'updated_at'>
  ): Promise<EscalationPolicy | null> => {
    if (!tenantId || !user) return null;

    setIsLoading(true);
    try {
      // TODO: Use EscalationRepository when integrated
      // const newPolicy = await escalationRepository.create(policy);
      const newPolicy: EscalationPolicy = {
        ...policy,
        escalation_id: `escalation_${Date.now()}`, // Temp - should use useGenerateId
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      setEscalationPolicies(prev => [...prev, newPolicy]);
      notifications.show({
        title: 'Success',
        message: 'Escalation policy created',
        color: 'green',
      });
      return newPolicy;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to create escalation policy';
      setError(errorMsg);
      notifications.show({
        title: 'Error',
        message: errorMsg,
        color: 'red',
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, user]);

  // ============================================================
  // Workflow Schedule Operations
  // ============================================================

  const getWorkflowSchedules = useCallback(async (): Promise<WorkflowSchedule[]> => {
    if (!tenantId) return [];

    setIsLoading(true);
    try {
      // TODO: Use ScheduleRepository when integrated
      // const schedules = await scheduleRepository.findByTenant(tenantId);
      const schedules: WorkflowSchedule[] = [];
      setWorkflowSchedules(schedules);
      return schedules;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to load workflow schedules';
      setError(errorMsg);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  const createWorkflowSchedule = useCallback(async (
    schedule: Omit<WorkflowSchedule, 'schedule_id' | 'created_at' | 'updated_at'>
  ): Promise<WorkflowSchedule | null> => {
    if (!tenantId || !user) return null;

    setIsLoading(true);
    try {
      // TODO: Use ScheduleRepository when integrated
      const newSchedule: WorkflowSchedule = {
        ...schedule,
        schedule_id: `schedule_${Date.now()}`, // Temp - should use useGenerateId
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      setWorkflowSchedules(prev => [...prev, newSchedule]);
      notifications.show({
        title: 'Success',
        message: 'Workflow schedule created',
        color: 'green',
      });
      return newSchedule;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to create workflow schedule';
      setError(errorMsg);
      notifications.show({
        title: 'Error',
        message: errorMsg,
        color: 'red',
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, user]);

  // ============================================================
  // Resource Reservation Operations
  // ============================================================

  const getResourceReservations = useCallback(async (): Promise<ResourceReservation[]> => {
    if (!tenantId) return [];

    setIsLoading(true);
    try {
      // TODO: Use ResourceRepository when integrated
      // const resources = await resourceRepository.findByTenant(tenantId);
      const resources: ResourceReservation[] = [];
      setResourceReservations(resources);
      return resources;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to load resource reservations';
      setError(errorMsg);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  const createResourceReservation = useCallback(async (
    resource: Omit<ResourceReservation, 'resource_id' | 'created_at' | 'updated_at'>
  ): Promise<ResourceReservation | null> => {
    if (!tenantId || !user) return null;

    setIsLoading(true);
    try {
      // TODO: Use ResourceRepository when integrated
      const newResource: ResourceReservation = {
        ...resource,
        resource_id: `resource_${Date.now()}`, // Temp - should use useGenerateId
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      setResourceReservations(prev => [...prev, newResource]);
      notifications.show({
        title: 'Success',
        message: 'Resource reservation created',
        color: 'green',
      });
      return newResource;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to create resource reservation';
      setError(errorMsg);
      notifications.show({
        title: 'Error',
        message: errorMsg,
        color: 'red',
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, user]);

  // ============================================================
  // Integration Config Operations
  // ============================================================

  const getIntegrationConfigs = useCallback(async (): Promise<IntegrationConfig[]> => {
    if (!tenantId) return [];

    setIsLoading(true);
    try {
      // TODO: Use IntegrationRepository when integrated
      // const integrations = await integrationRepository.findByTenant(tenantId);
      const integrations: IntegrationConfig[] = [];
      setIntegrationConfigs(integrations);
      return integrations;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to load integration configs';
      setError(errorMsg);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  const triggerHealthCheck = useCallback(async (
    integrationId: string
  ): Promise<IntegrationConfig | null> => {
    if (!tenantId) return null;

    setIsLoading(true);
    try {
      // TODO: Use IntegrationRepository when integrated
      // const updated = await integrationRepository.triggerHealthCheck(integrationId);
      notifications.show({
        title: 'Success',
        message: 'Health check triggered',
        color: 'green',
      });
      return null;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to trigger health check';
      setError(errorMsg);
      notifications.show({
        title: 'Error',
        message: errorMsg,
        color: 'red',
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  // ============================================================
  // Policy Rule Operations
  // ============================================================

  const getPolicyRules = useCallback(async (policyType?: string): Promise<PolicyRule[]> => {
    if (!tenantId) return [];

    setIsLoading(true);
    try {
      const repository = createPolicyRepository(apolloClient);
      const policies = await repository.listByTenant(tenantId, policyType);
      setPolicyRules(policies);
      return policies;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to load policy rules';
      setError(errorMsg);
      notifications.show({
        title: 'Error',
        message: errorMsg,
        color: 'red',
      });
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, apolloClient]);

  const createPolicyRule = useCallback(async (
    input: Omit<CreatePolicyRuleInput, 'tenantId'>
  ): Promise<PolicyRule | null> => {
    if (!tenantId) return null;

    setIsLoading(true);
    try {
      const repository = createPolicyRepository(apolloClient);
      const newPolicy = await repository.create({
        ...input,
        tenantId: tenantId,
      });
      setPolicyRules(prev => [...prev, newPolicy]);
      notifications.show({
        title: 'Success',
        message: 'Policy rule created',
        color: 'green',
      });
      return newPolicy;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to create policy rule';
      setError(errorMsg);
      notifications.show({
        title: 'Error',
        message: errorMsg,
        color: 'red',
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, apolloClient]);

  const updatePolicyRule = useCallback(async (
    input: Omit<UpdatePolicyRuleInput, 'tenantId'>
  ): Promise<PolicyRule | null> => {
    if (!tenantId) return null;

    setIsLoading(true);
    try {
      const repository = createPolicyRepository(apolloClient);
      const updatedPolicy = await repository.update({
        ...input,
        tenantId: tenantId,
      });
      setPolicyRules(prev =>
        prev.map(p => (p.policy_id === updatedPolicy.policy_id ? updatedPolicy : p))
      );
      notifications.show({
        title: 'Success',
        message: 'Policy rule updated',
        color: 'green',
      });
      return updatedPolicy;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to update policy rule';
      setError(errorMsg);
      notifications.show({
        title: 'Error',
        message: errorMsg,
        color: 'red',
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, apolloClient]);

  const deletePolicyRule = useCallback(async (policyId: string): Promise<boolean> => {
    if (!tenantId) return false;

    setIsLoading(true);
    try {
      const repository = createPolicyRepository(apolloClient);
      const success = await repository.delete({
        policyId,
        tenantId: tenantId,
      });
      if (success) {
        setPolicyRules(prev => prev.filter(p => p.policy_id !== policyId));
        notifications.show({
          title: 'Success',
          message: 'Policy rule deleted',
          color: 'green',
        });
      }
      return success;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to delete policy rule';
      setError(errorMsg);
      notifications.show({
        title: 'Error',
        message: errorMsg,
        color: 'red',
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, apolloClient]);

  const dryRunPolicy = useCallback(async (
    policyId: string,
    testData: Record<string, any>
  ): Promise<DryRunResult | null> => {
    if (!tenantId) return null;

    setIsLoading(true);
    try {
      const repository = createPolicyRepository(apolloClient);
      const result = await repository.dryRun({
        policyId,
        tenantId: tenantId,
        testData,
      });
      return result;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to run policy dry-run';
      setError(errorMsg);
      notifications.show({
        title: 'Error',
        message: errorMsg,
        color: 'red',
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, apolloClient]);

  return {
    // State
    escalationPolicies,
    workflowSchedules,
    resourceReservations,
    integrationConfigs,
    policyRules,
    isLoading,
    error,

    // Escalation Operations
    getEscalationPolicies,
    createEscalationPolicy,

    // Schedule Operations
    getWorkflowSchedules,
    createWorkflowSchedule,

    // Resource Operations
    getResourceReservations,
    createResourceReservation,

    // Integration Operations
    getIntegrationConfigs,
    triggerHealthCheck,

    // Policy Operations
    getPolicyRules,
    createPolicyRule,
    updatePolicyRule,
    deletePolicyRule,
    dryRunPolicy,
  };
}
